import React, { useState, useEffect } from 'react'
import { useImproveCycleStore } from '../../store/improveCycleStore'
import { Save, CheckCircle, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react'

const VerificarForm = ({ ciclo }) => {
  const { fetchDashboardVerificar, saveVerificar, completarVerificar, loading } = useImproveCycleStore()
  const [dashboardData, setDashboardData] = useState(null)
  const [formData, setFormData] = useState({
    conclusiones_analisis: '',
    recomendacion: ''
  })
  const [mensaje, setMensaje] = useState(null)

  useEffect(() => {
    if (ciclo?.id) {
      loadDashboard()
    }
    if (ciclo?.fases?.verificar) {
      setFormData({
        conclusiones_analisis: ciclo.fases.verificar.conclusiones_analisis || '',
        recomendacion: ciclo.fases.verificar.recomendacion || ''
      })
    }
  }, [ciclo])

  const loadDashboard = async () => {
    try {
      const data = await fetchDashboardVerificar(ciclo.id)
      setDashboardData(data)
    } catch (error) {
      console.error('Error loading dashboard:', error)
    }
  }

  const handleSave = async () => {
    try {
      await saveVerificar(ciclo.id, formData)
      setMensaje({ type: 'success', text: 'Guardado exitosamente' })
      setTimeout(() => setMensaje(null), 3000)
    } catch (error) {
      setMensaje({ type: 'error', text: error.message })
    }
  }

  const handleCompletar = async () => {
    try {
      await completarVerificar(ciclo.id)
      setMensaje({ type: 'success', text: 'Fase Verificar completada. Fase Actuar desbloqueada!' })
    } catch (error) {
      setMensaje({ type: 'error', text: error.message })
    }
  }

  const puedeCompletar = formData.conclusiones_analisis.length >= 50 && formData.recomendacion && !ciclo?.fases?.verificar?.completado

  return (
    <div className="space-y-6">
      {mensaje && (
        <div className={`p-4 rounded-lg flex items-start gap-3 ${
          mensaje.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          {mensaje.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          )}
          <p className={mensaje.type === 'success' ? 'text-green-700' : 'text-red-700'}>
            {mensaje.text}
          </p>
        </div>
      )}

      {/* Dashboard de Comparación */}
      {dashboardData && (
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold mb-4">Dashboard de Comparación</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600 mb-1">Objetivo</p>
              <p className="text-3xl font-bold text-blue-600">{dashboardData.metrica_objetivo}</p>
              <p className="text-xs text-gray-500 mt-1">{dashboardData.metrica_nombre}</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600 mb-1">Real</p>
              <p className="text-3xl font-bold text-green-600">{dashboardData.metrica_real}</p>
              <p className="text-xs text-gray-500 mt-1">{dashboardData.metrica_nombre}</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600 mb-1">Desviación</p>
              <div className="flex items-center gap-2">
                <p className={`text-3xl font-bold ${
                  dashboardData.objetivo_alcanzado ? 'text-green-600' : 'text-red-600'
                }`}>
                  {Math.abs(dashboardData.desviacion_porcentaje).toFixed(1)}%
                </p>
                {dashboardData.objetivo_alcanzado ? (
                  <TrendingDown className="w-6 h-6 text-green-600" />
                ) : (
                  <TrendingUp className="w-6 h-6 text-red-600" />
                )}
              </div>
            </div>
          </div>

          {/* Interpretación */}
          <div className={`p-4 rounded-lg ${
            dashboardData.objetivo_alcanzado ? 'bg-green-100 border border-green-300' : 'bg-yellow-100 border border-yellow-300'
          }`}>
            <p className={`font-semibold ${
              dashboardData.objetivo_alcanzado ? 'text-green-800' : 'text-yellow-800'
            }`}>
              {dashboardData.interpretacion}
            </p>
          </div>

          {/* Gráfico simple */}
          <div className="mt-6">
            <div className="flex gap-4 items-end h-48">
              <div className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full bg-blue-500 rounded-t-lg transition-all duration-500"
                  style={{ height: `${(dashboardData.metrica_objetivo / Math.max(dashboardData.metrica_objetivo, dashboardData.metrica_real)) * 100}%` }}
                ></div>
                <p className="text-sm font-semibold mt-2">Objetivo</p>
              </div>
              <div className="flex-1 flex flex-col items-center">
                <div 
                  className={`w-full rounded-t-lg transition-all duration-500 ${
                    dashboardData.objetivo_alcanzado ? 'bg-green-500' : 'bg-red-500'
                  }`}
                  style={{ height: `${(dashboardData.metrica_real / Math.max(dashboardData.metrica_objetivo, dashboardData.metrica_real)) * 100}%` }}
                ></div>
                <p className="text-sm font-semibold mt-2">Real</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formulario de Conclusiones */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">Análisis y Conclusiones</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Conclusiones del Análisis * (mínimo 50 caracteres)
            </label>
            <textarea
              value={formData.conclusiones_analisis}
              onChange={(e) => setFormData(prev => ({ ...prev, conclusiones_analisis: e.target.value }))}
              disabled={ciclo?.fases?.verificar?.completado}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="Analiza los resultados obtenidos comparados con el objetivo..."
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.conclusiones_analisis.length} / 50 caracteres mínimos
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recomendación *
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="recomendacion"
                  value="estandarizar"
                  checked={formData.recomendacion === 'estandarizar'}
                  onChange={(e) => setFormData(prev => ({ ...prev, recomendacion: e.target.value }))}
                  disabled={ciclo?.fases?.verificar?.completado}
                  className="w-4 h-4"
                />
                <div>
                  <p className="font-semibold text-gray-800">✅ Estandarizar</p>
                  <p className="text-sm text-gray-600">El objetivo fue alcanzado, generar POE</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="recomendacion"
                  value="re_planificar"
                  checked={formData.recomendacion === 're_planificar'}
                  onChange={(e) => setFormData(prev => ({ ...prev, recomendacion: e.target.value }))}
                  disabled={ciclo?.fases?.verificar?.completado}
                  className="w-4 h-4"
                />
                <div>
                  <p className="font-semibold text-gray-800">🔄 Re-planificar</p>
                  <p className="text-sm text-gray-600">El objetivo no fue alcanzado, crear nuevo ciclo</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {!ciclo?.fases?.verificar?.completado && (
          <button
            onClick={handleSave}
            disabled={loading}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300"
          >
            Guardar Análisis
          </button>
        )}
      </div>

      {/* Botón Completar */}
      {!ciclo?.fases?.verificar?.completado && (
        <div className="flex gap-4">
          <button
            onClick={handleCompletar}
            disabled={loading || !puedeCompletar}
            className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-5 h-5" />
            Completar Verificación
          </button>
          {!puedeCompletar && (
            <p className="text-sm text-gray-600 flex items-center">
              Completa las conclusiones (min 50 caracteres) y selecciona una recomendación
            </p>
          )}
        </div>
      )}

      {ciclo?.fases?.verificar?.completado && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700 font-semibold">✓ Fase Verificar completada</p>
        </div>
      )}
    </div>
  )
}

export default VerificarForm
